import { Controller } from "@hotwired/stimulus"
import RequestHelper from "../helpers/request_helper"

function fetchJson(url, options) {
  return fetch(url, options).then((response) => response.json().then((data) => ({ ok: response.ok, data })));
}

// Paired with the shared `datatable` controller on the same mount: that one owns the DataTables
// lifecycle, this one owns the credit-transfer toolbar, the filter-order search reload and the
// add-new-team seat-limit gate. The team modal and role selection belong to
// team_editor_controller.js and team_member_roles_controller.js, mounted alongside this one.
export default class extends Controller {
  static get targets() {
    return [
      "table", "buttonFooter", "transferCreditsButton", "availableBalance",
      "availablePlatformBalance",
    ];
  }

  connect() {
    this.onOrderChange = this.onOrderChange.bind(this);
    this.onFilterOrderKeyup = this.onFilterOrderKeyup.bind(this);

    this.currentCreditsAssigned = 0;
    this.currentPlatformCreditsAssigned = 0;

    // #filter_order renders on no current view; kept as a delegated find() so the absent-element
    // no-op survives - see the comment on onFilterOrderKeyup itself.
    $(this.element).find("#filter_order").on("keyup.teamTable", this.onFilterOrderKeyup);

    if (this.hasTableTarget) {
      $(this.tableTarget).on("order.dt.teamTable", this.onOrderChange);

      if (!this.platformLicenseEnforced()) {
        $(this.tableTarget).DataTable().column(5).visible(false);
      }
    }

    const url = new URL(window.location.href);
    if (new URLSearchParams(url.search).has("new")) {
      // Page-global: the trigger this opens lives in the dashboard header/members menu on some
      // mounts and in this cluster's own per-row dropdown on others, never both at once in
      // practice, so all-matches (not just the first) preserves the original jQuery trigger call.
      document.querySelectorAll(".open-team-modal.from-dashboard").forEach((el) => el.click());
    }
  }

  disconnect() {
    $(this.element).find("#filter_order").off(".teamTable");
    if (this.hasTableTarget) { $(this.tableTarget).off(".teamTable"); }
  }

  // Iterates the credit/platform-credit `<span>` cells DataTables renders into the table body.
  // These rows never exist at connect() - they arrive later via the shared `datatable`
  // controller's ajax draw - so every call site re-queries live, matching the jQuery find()s this
  // replaces: querySelectorAll returns an empty NodeList (not an error) when no rows are loaded
  // yet or the mount has none, the same no-op the jQuery version gave for free.
  eachIn(selector, callback) {
    this.element.querySelectorAll(selector).forEach(callback);
  }

  platformLicenseEnforced() {
    return this.hasTableTarget && this.tableTarget.dataset.platformLicenseEnforced === "true";
  }

  onOrderChange() { this.hideTransferToolbar(); }

  // `#filter_order` renders on no current view; this is dead today.
  onFilterOrderKeyup(event) {
    clearTimeout(this.filterOrderTimer);
    this.filterOrderTimer = setTimeout(() => this.reload(), 500);
  }

  // This call site never passes params, so rewriting the ajax source would just set the same URL
  // again - a plain ajax.reload() is equivalent.
  reload() {
    if (!this.hasTableTarget) { return; }
    $(this.tableTarget).DataTable().ajax.reload();
  }

  updateCreditsMenu(credits) {
    const el = document.querySelector(".credits-menu .organization-account-balance");
    if (el) { el.textContent = credits.toLocaleString(); }
  }

  creditSum() {
    let credits = 0;
    this.eachIn("tr td.credits span", (el) => {
      if (el.dataset.creditManagement === "organization_credits") { return; }
      credits += parseFloat(el.querySelector("input")?.value || 0);
    });
    return credits;
  }

  platformCreditSum() {
    let credits = 0;
    this.eachIn("tr td.platform_credits span", (el) => {
      if (el.dataset.creditManagement === "organization_credits") { return; }
      credits += parseFloat(el.querySelector("input")?.value || 0);
    });
    return credits;
  }

  // `text-danger` is a Bootstrap utility class used across the whole app (D1), not a class this
  // controller owns - left as a literal rather than moved onto the Classes API.
  adjustCredits() {
    if (!this.hasAvailableBalanceTarget) { return; }

    const availableCredits = parseFloat(this.availableBalanceTarget.dataset.credits);
    const inputValue = this.creditSum();
    const newValue = (availableCredits + this.currentCreditsAssigned) - inputValue;

    if (newValue < 0) {
      this.availableBalanceTarget.textContent = "0";
      this.availableBalanceTarget.classList.add("text-danger");
    } else if (newValue > (availableCredits + this.currentCreditsAssigned)) {
      this.availableBalanceTarget.textContent = availableCredits.toLocaleString();
      this.availableBalanceTarget.classList.add("text-danger");
    } else {
      this.availableBalanceTarget.textContent = newValue.toLocaleString();
      this.availableBalanceTarget.classList.remove("text-danger");
    }
  }

  adjustPlatformCredits() {
    if (!this.hasAvailablePlatformBalanceTarget) { return; }

    const availableCredits = parseFloat(this.availablePlatformBalanceTarget.dataset.credits);
    const inputValue = this.platformCreditSum();
    const newValue = (availableCredits + this.currentPlatformCreditsAssigned) - inputValue;

    if (newValue < 0) {
      this.availablePlatformBalanceTarget.textContent = "0";
      this.availablePlatformBalanceTarget.classList.add("text-danger");
    } else if (newValue > (availableCredits + this.currentPlatformCreditsAssigned)) {
      this.availablePlatformBalanceTarget.textContent = availableCredits.toLocaleString();
      this.availablePlatformBalanceTarget.classList.add("text-danger");
    } else {
      this.availablePlatformBalanceTarget.textContent = newValue.toLocaleString();
      this.availablePlatformBalanceTarget.classList.remove("text-danger");
    }
  }

  hideTransferToolbar() {
    if (this.hasButtonFooterTarget) { this.buttonFooterTarget.classList.remove("active"); }
    if (this.hasTransferCreditsButtonTarget) { this.transferCreditsButtonTarget.style.display = ""; }
    this.resetCredits();
  }

  resetCredits() {
    this.eachIn("tr td.credits span", (el) => {
      if (el.dataset.creditManagement === "organization_credits") { return; }
      el.textContent = parseFloat(el.dataset.credits).toLocaleString();
    });
    this.eachIn("tr td.platform_credits span", (el) => {
      if (el.dataset.creditManagement === "organization_credits") { return; }
      el.textContent = parseFloat(el.dataset.credits).toLocaleString();
    });

    if (this.hasAvailableBalanceTarget) {
      this.availableBalanceTarget.textContent = parseFloat(this.availableBalanceTarget.dataset.credits).toLocaleString();
    }
    if (this.hasAvailablePlatformBalanceTarget) {
      this.availablePlatformBalanceTarget.textContent = parseFloat(this.availablePlatformBalanceTarget.dataset.credits).toLocaleString();
    }
  }

  onCancelTransfer(event) {
    this.hideTransferToolbar();
  }

  onCreditInputChange(event) {
    const amount = parseInt(event.target.value);
    if (isNaN(amount) || amount < 0) { event.target.value = 0; }
    this.adjustCredits();
  }

  onPlatformCreditInputChange(event) {
    const amount = parseInt(event.target.value);
    if (isNaN(amount) || amount < 0) { event.target.value = 0; }
    this.adjustPlatformCredits();
  }

  // Builds the credit-transfer <input> as a real element (rather than an interpolated HTML
  // string) so it can carry its own data-action - Stimulus wires it up the moment it lands in the
  // DOM, which is what lets connect() skip a delegated jQuery listener for these two classes.
  buildTransferInput(className, action, name, value) {
    const input = document.createElement("input");
    input.type = "number";
    input.min = "0";
    input.className = `form-control ${className}`;
    input.name = name;
    input.value = value;
    input.setAttribute("data-action", action);
    return input;
  }

  onOpenTransferToolbar(event) {
    event.preventDefault();
    event.currentTarget.style.display = "none";
    if (this.hasButtonFooterTarget) { this.buttonFooterTarget.classList.add("active"); }
    this.currentCreditsAssigned = 0;
    this.currentPlatformCreditsAssigned = 0;

    this.eachIn("tr td.credits span", (el) => {
      if (el.dataset.creditManagement === "organization_credits") { return; }
      const creditAmount = parseFloat(el.dataset.credits);
      this.currentCreditsAssigned += creditAmount;
      el.replaceChildren(this.buildTransferInput(
        "credit-transfer-input",
        "keyup->team-table#adjustCredits change->team-table#onCreditInputChange",
        el.dataset.teamId,
        creditAmount,
      ));
    });

    this.eachIn("tr td.platform_credits span", (el) => {
      if (el.dataset.creditManagement === "organization_credits") { return; }
      const creditAmount = parseFloat(el.dataset.credits);
      this.currentPlatformCreditsAssigned += creditAmount;
      el.replaceChildren(this.buildTransferInput(
        "platform-credit-transfer-input",
        "keyup->team-table#adjustPlatformCredits change->team-table#onPlatformCreditInputChange",
        `${el.dataset.teamId}-pc`,
        creditAmount,
      ));
    });
  }

  onSubmitTransfer(event) {
    const params = { transfer_credit: {}, transfer_platform_credit: {} };
    const url = event.currentTarget.dataset.url;

    this.eachIn("tr td.credits span", (el) => {
      const input = el.querySelector("input");
      if (el.dataset.creditManagement === "organization_credits") { return; }
      params.transfer_credit[el.dataset.teamId] = input.value;
    });

    this.eachIn("tr td.platform_credits span", (el) => {
      const input = el.querySelector("input");
      if (el.dataset.creditManagement === "organization_credits") { return; }
      if (el.dataset.credits === String(parseInt(input.value))) { return; }
      params.transfer_platform_credit[el.dataset.teamId] = input.value;
    });

    const options = RequestHelper.defaultPostOptions();
    options.headers = Object.assign({}, options.headers, { "Content-Type": "application/json" });
    options.body = JSON.stringify(params);

    fetchJson(url, options).then(({ ok, data }) => {
      if (!ok) {
        window.ltAlert({ message: data.message || "An error occurred while transferring credits. Transfer incomplete.", type: "toast-error", duration: 2500 });
        return;
      }

      window.ltAlert({ message: data.message, duration: 2500 });

      this.eachIn("tr td.credits span", (el) => {
        const input = el.querySelector("input");
        if (el.dataset.creditManagement === "organization_credits") { return; }
        const value = parseFloat(input.value);
        el.dataset.credits = value;
        el.textContent = value.toLocaleString();
      });

      this.eachIn("tr td.platform_credits span", (el) => {
        const input = el.querySelector("input");
        if (el.dataset.creditManagement === "organization_credits") { return; }
        const value = parseFloat(input.value);
        el.dataset.credits = value;
        el.textContent = value.toLocaleString();
      });

      if (this.hasButtonFooterTarget) { this.buttonFooterTarget.classList.remove("active"); }
      if (this.hasTransferCreditsButtonTarget) { this.transferCreditsButtonTarget.style.display = ""; }

      if (this.hasAvailableBalanceTarget) { this.availableBalanceTarget.dataset.credits = data.account_balance; }
      if (this.hasAvailablePlatformBalanceTarget) { this.availablePlatformBalanceTarget.dataset.credits = data.platform_credit_balance; }

      const creditsMenu = document.querySelector(".credits-menu");
      if (creditsMenu) { this.updateCreditsMenu(data.account_balance); }
    });
  }

  onAddNewTeam(event) {
    const canCreateTeam = event.currentTarget.getAttribute("href") !== "#";

    if (!canCreateTeam) {
      event.preventDefault();
      $("#team-limit-reached-modal").modal("show");
    }
  }

  onDeleteTeamButton(event) {
    event.preventDefault();
  }
}
